import React, { useState, useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";

const ExpenseAllocation = () => {
  // State for dropdown options
  const [vendors, setVendors] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [yearGroups, setYearGroups] = useState([]);
  const [classesData, setClassesData] = useState([]);
  const [classId, setClassesId] = useState([]);
  const [sections, setSections] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [allocationBasis, setAllocationBasis] = useState("");
  const [studentsData, setStudentsData] = useState([]);

  // Static categories
  const categories = [
    { id: 1, name: "Vendor Payment" },
    { id: 2, name: "Cash Payment" },
  ];

  // Form setup
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      allocationDate: new Date().toISOString().split("T")[0],
      category: null,
      vendor: null,
      totalAmount: 0,
      remainingAmount: 0,
      description: "",
      expenseAccount: null,
      splitLevel1: null,
      splitLevel2: [],
      splitLevel3: [],
      splitAmount: 0,
      splitDescription: "",
    },
  });

  const formValues = watch();
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const centerId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  // Calculate remaining amount
  const remainingAmount = useMemo(() => {
    const allocated = allocations.reduce((sum, item) => sum + item.debit, 0);
    return (formValues.totalAmount || 0) - allocated;
  }, [allocations, formValues.totalAmount]);

  // Update remaining amount in form
  useEffect(() => {
    setValue("remainingAmount", remainingAmount);
  }, [remainingAmount, setValue]);

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Simulate API calls
        const vendorsResponse = [
          { id: 1, name: "Vendor 1" },
          { id: 2, name: "Vendor 2" },
          { id: 3, name: "Vendor 3" },
        ];

        const yearGroupsResponse = [
          { id: 14, name: "Sr. Boys" },
          { id: 15, name: "Sr. Girls" },
        ];

        setVendors(vendorsResponse);
        setYearGroups(yearGroupsResponse);
        await fetchClassesByCenterId(centerId || 3);
        await fetchChartOfAccount();
        setIsLoading(false);
      } catch (error) {
        toast.error("Failed to load initial data");
        console.error(error);
      }
    };

    fetchInitialData();
  }, [centerId]);

  // Fetch classes by center ID
  const fetchClassesByCenterId = async (centerId) => {
    try {
      if (centerId) {
        const response = await get(
          `CenterClass/GetClassCenterByCenterId?id=${centerId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setClassesData(response);
      } else {
        setClassesData([]);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };
  const fetchChartOfAccount = async () => {
    try {
      const response = await get(`AdminParentChat/GetChartOfAccounts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAccounts(response);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  // Fetch sections when classes change
  useEffect(() => {
    const fetchSections = async () => {
      const classIds = formValues.splitLevel2?.map((c) => c.value) || [];
      const classLable = formValues.splitLevel2?.map((c) => c.label) || [];
      setClassesId(classLable);
      if (classIds.length > 0) {
        try {
          const response = await get(
            `ClassSection/GetAllSectionByCenterClassIdAndCenterIds?centerClassId=${classIds.join(
              ","
            )}&centerId=${centerId || 3}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setSections(response);
        } catch (error) {
          console.error("Error fetching sections:", error);
          setSections([]);
        }
      } else {
        setSections([]);
      }
    };

    fetchSections();
  }, [formValues.splitLevel2, centerId, token]);

  // Fetch students when sections change
  useEffect(() => {
    const fetchStudents = async () => {
      const sectionIds = formValues.splitLevel3?.map((s) => s.value) || [];

      if (sectionIds.length > 0) {
        try {
          const response = await get(
            `Student/GetAllStudentBySectionIdAndCenterId?sectionIds=${sectionIds.join(
              ","
            )}&centerId=${centerId || 3}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setStudentsData(response);
        } catch (error) {
          console.error("Error fetching students:", error);
          setStudentsData([]);
        }
      } else {
        setStudentsData([]);
      }
    };

    fetchStudents();
  }, [formValues.splitLevel3, centerId, token]);

  // Handle category change
  const handleCategoryChange = (selectedOption) => {
    setValue("category", selectedOption);
    setValue("vendor", null);
  };

  // Add allocation line
  const handleAddAllocation = async () => {
    // Validation
    if (!formValues.splitLevel3 || formValues.splitLevel3.length === 0) {
      toast.error("Please select at least one section");
      return;
    }

    if (
      !formValues.expenseAccount ||
      !formValues.splitLevel1 ||
      !formValues.splitAmount ||
      !allocationBasis
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    const splitAmount = parseFloat(formValues.splitAmount) || 0;

    if (splitAmount <= 0) {
      toast.error("Split amount must be greater than 0");
      return;
    }

    if (splitAmount > remainingAmount) {
      toast.error("Split amount cannot exceed remaining amount");
      return;
    }

    if (studentsData.length === 0) {
      toast.error("No students found in selected sections");
      return;
    }

    try {
      // Calculate total students in selected sections
      const selectedSectionIds = formValues.splitLevel3.map((s) => s.value);
      const totalStudents = studentsData.filter((student) =>
        selectedSectionIds.includes(student.classSectionId)
      ).length;

      if (totalStudents === 0) {
        toast.error("No students found in selected sections");
        return;
      }

      // Create allocations for each selected section
      const newAllocations = formValues.splitLevel3
        .map((section) => {
          const sectionId = section.value;
          const sectionStudents = studentsData.filter(
            (student) => student.classSectionId === sectionId
          ).length;

          if (sectionStudents === 0) return null;

          // Calculate amount for this section based on student strength
          const sectionAmount = parseFloat(
            ((splitAmount * sectionStudents) / totalStudents).toFixed(2)
          );
          const percentage = parseFloat(
            ((sectionAmount / formValues.totalAmount) * 100).toFixed(2)
          );
          return {
            id: `${sectionId}-${Date.now()}`,
            account: `${formValues.expenseAccount.label}-${formValues.splitLevel1.label}-${section.label}`,
            description: formValues.splitDescription || "Expense Allocation",
            debit: sectionAmount,
            credit: null,
            allocationBasis,
            sectionId,
            sectionName: section.label,
            studentCount: sectionStudents,
            percentages: percentage,
          };
        })
        .filter(Boolean);

      if (newAllocations.length === 0) {
        toast.error("No valid allocations could be created");
        return;
      }

      setAllocations([...allocations, ...newAllocations]);

      // Reset split fields
      //setValue("splitAmount", 0);
      //setValue("splitDescription", "");
      setAllocationBasis("");
    } catch (error) {
      console.error("Error creating allocations:", error);
      toast.error("Failed to create allocations");
    }
  };

  // Remove allocation line
  const handleRemoveAllocation = (id) => {
    setAllocations(allocations.filter((item) => item.id !== id));
  };

  // Submit form
  const onSubmit = async (data) => {
    if (allocations.length === 0) {
      toast.error("Please add at least one allocation");
      return;
    }
    console.log(allocations);
    if (remainingAmount > 0) {
      toast.error("Please allocate the entire amount");
      return;
    }
    try {
      const payload = {
        header: {
          date: data.allocationDate,
          categoryId: data.category.id,
          vendorId: data.vendor?.id || null,
          totalAmount: data.totalAmount,
          description: data.description,
          centerId: centerId,
          organizationId: organizationId,
        },
        details: allocations.map((allocation, index) => ({
          lineNumber: index + 1,
          accountId: data.expenseAccount.id,
          description: allocation.description,
          debit: allocation.debit,
          vendorCashId: data.category.id,
          credit: allocation.credit,
          allocationBasis: allocation.allocationBasis,
          sectionId: allocation.sectionId,
          percentages: allocation.percentages,
          className: classId[0],
          sectionName: allocation.sectionName,
          classCategoryId: data.splitLevel1.id,
          studentCount: allocation.studentCount,
        })),
      };
      // Replace with your actual API endpoint
      const response = await post(
        "AdminParentChat/SaveExpenseAllocation",
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.isSuccess) {
        toast.success("Expense allocation saved successfully");
      }

      reset();
      setAllocations([]);
      setAllocationBasis("");
    } catch (error) {
      console.error("Error submitting expense allocation:", error);
      toast.error("Failed to save expense allocation");
    }
  };

  if (isLoading) {
    return <div className="fa fa-spinner fs-7"></div>;
  }

  return (
    <div className="container-fluid">
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Expense Allocation</h2>
      </div>

      {/* Header Card */}
      <div className="card mb-4">
        <div className="card-header">
          <h5>Expense Allocation Header</h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            {/* Date Field */}
            <div className="col-md-3">
              <label className="form-label">
                Date<span className="text-danger fs-9">*</span>
              </label>
              <input
                {...register("allocationDate", {
                  required: "This field is required",
                  validate: (value) => {
                    const selectedDate = new Date(value);
                    const today = new Date();
                    return (
                      selectedDate <= today || "Date cannot be in the future"
                    );
                  },
                })}
                type="date"
                className={`form-control ${
                  errors.allocationDate ? "is-invalid" : ""
                }`}
                max={new Date().toISOString().split("T")[0]}
              />
              {errors.allocationDate && (
                <small className="text-danger">
                  {errors.allocationDate.message}
                </small>
              )}
            </div>

            {/* Category Field */}
            <div className="col-md-3">
              <label className="form-label">
                Category<span className="text-danger fs-9">*</span>
              </label>
              <Controller
                name="category"
                control={control}
                rules={{ required: "Category is required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={categories.map((c) => ({
                      value: c.id,
                      label: c.name,
                      id: c.id,
                    }))}
                    onChange={(option) => {
                      handleCategoryChange(option);
                      field.onChange(option);
                    }}
                    className={`react-select ${
                      errors.category ? "is-invalid" : ""
                    }`}
                    classNamePrefix="react-select"
                    placeholder="Select category"
                  />
                )}
              />
              {errors.category && (
                <small className="text-danger">{errors.category.message}</small>
              )}
            </div>

            {/* Vendor Field */}
            <div className="col-md-3">
              <label className="form-label">
                Vendor
                {formValues.category?.id === 1 && (
                  <span className="text-danger fs-9">*</span>
                )}
              </label>
              <Controller
                name="vendor"
                control={control}
                rules={{
                  required:
                    formValues.category?.id === 1
                      ? "Vendor is required"
                      : false,
                }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={vendors.map((v) => ({
                      value: v.id,
                      label: v.name,
                      id: v.id,
                    }))}
                    isDisabled={
                      !formValues.category || formValues.category.id !== 1
                    }
                    className={`react-select ${
                      errors.vendor ? "is-invalid" : ""
                    }`}
                    classNamePrefix="react-select"
                    placeholder="Select vendor"
                  />
                )}
              />
              {errors.vendor && (
                <small className="text-danger">{errors.vendor.message}</small>
              )}
            </div>

            {/* Total Amount Field */}
            <div className="col-md-3">
              <label className="form-label">
                Total Amount<span className="text-danger fs-9">*</span>
              </label>
              <input
                {...register("totalAmount", {
                  required: "Amount is required",
                  min: { value: 0.01, message: "Amount must be positive" },
                })}
                type="number"
                step="0.01"
                min="0.01"
                className={`form-control ${
                  errors.totalAmount ? "is-invalid" : ""
                }`}
              />
              {errors.totalAmount && (
                <small className="text-danger">
                  {errors.totalAmount.message}
                </small>
              )}
            </div>

            {/* Remaining Amount Field */}
            <div className="col-md-3">
              <label className="form-label">Remaining Amount</label>
              <input
                type="text"
                className="form-control"
                value={remainingAmount.toFixed(2)}
                readOnly
              />
            </div>

            {/* Description Field */}
            <div className="col-md-9">
              <label className="form-label">Description</label>
              <input
                {...register("description")}
                type="text"
                className="form-control"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Details Card */}
      <div className="card mb-4">
        <div className="card-header">
          <h5>Expense Allocation Details</h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            {/* Expense Account Field */}
            <div className="col-md-3">
              <label className="form-label">
                Expense Account<span className="text-danger fs-9">*</span>
              </label>
              <Controller
                name="expenseAccount"
                control={control}
                rules={{ required: "Account is required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={accounts.map((a) => ({
                      value: a.id,
                      label: a.name,
                      id: a.id,
                    }))}
                    className={`react-select ${
                      errors.expenseAccount ? "is-invalid" : ""
                    }`}
                    classNamePrefix="react-select"
                    placeholder="Select account"
                  />
                )}
              />
              {errors.expenseAccount && (
                <small className="text-danger">
                  {errors.expenseAccount.message}
                </small>
              )}
            </div>

            {/* Class Category Field */}
            <div className="col-md-3">
              <label className="form-label">
                Class Category<span className="text-danger fs-9">*</span>
              </label>
              <Controller
                name="splitLevel1"
                control={control}
                rules={{ required: "Class category is required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={yearGroups.map((g) => ({
                      value: g.id,
                      label: g.name,
                      id: g.id,
                    }))}
                    onChange={(option) => {
                      field.onChange(option);
                      setValue("splitLevel2", []);
                      setValue("splitLevel3", []);
                    }}
                    className={`react-select ${
                      errors.splitLevel1 ? "is-invalid" : ""
                    }`}
                    classNamePrefix="react-select"
                    placeholder="Select category"
                  />
                )}
              />
              {errors.splitLevel1 && (
                <small className="text-danger">
                  {errors.splitLevel1.message}
                </small>
              )}
            </div>

            {/* Year Group Field */}
            <div className="col-md-3">
              <label className="form-label">Year Group</label>
              <Controller
                name="splitLevel2"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={classesData.map((c) => ({
                      value: c.id,
                      label: c.name,
                      id: c.id,
                    }))}
                    isMulti
                    isClearable
                    closeMenuOnSelect={false}
                    onChange={(option) => {
                      field.onChange(option);
                      setValue("splitLevel3", []);
                    }}
                    className="react-select"
                    classNamePrefix="react-select"
                    placeholder="Select year groups"
                  />
                )}
              />
            </div>

            {/* Section Field */}
            <div className="col-md-3">
              <label className="form-label">Section</label>
              <Controller
                name="splitLevel3"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={sections.map((s) => ({
                      value: s.id,
                      label: s.name,
                      id: s.id,
                    }))}
                    isMulti
                    isClearable
                    closeMenuOnSelect={false}
                    className="react-select"
                    classNamePrefix="react-select"
                    placeholder="Select sections"
                  />
                )}
              />
            </div>

            {/* Split Amount */}
            <div className="col-md-2">
              <label className="form-label">
                Split Amount<span className="text-danger fs-9">*</span>
              </label>
              <input
                {...register("splitAmount", {
                  required: "Split amount is required",
                  min: { value: 0.01, message: "Amount must be positive" },
                })}
                type="number"
                step="0.01"
                min="0.01"
                className={`form-control ${
                  errors.splitAmount ? "is-invalid" : ""
                }`}
              />
              {errors.splitAmount && (
                <small className="text-danger">
                  {errors.splitAmount.message}
                </small>
              )}
            </div>

            {/* Allocation Basis */}
            <div className="col-md-4">
              <label className="form-label d-block">
                Allocation Basis<span className="text-danger fs-9">*</span>
              </label>
              <div className="d-flex gap-4">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="allocationBasis"
                    id="studentStrength"
                    value="studentStrength"
                    checked={allocationBasis === "studentStrength"}
                    onChange={() => setAllocationBasis("studentStrength")}
                  />
                  <label className="form-check-label" htmlFor="studentStrength">
                    Student Strength
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="allocationBasis"
                    id="classCapacity"
                    value="classCapacity"
                    checked={allocationBasis === "classCapacity"}
                    onChange={() => setAllocationBasis("classCapacity")}
                  />
                  <label className="form-check-label" htmlFor="classCapacity">
                    Class Capacity
                  </label>
                </div>
              </div>
              {!allocationBasis && (
                <small className="text-danger">
                  Please select an allocation basis
                </small>
              )}
            </div>

            {/* Split Description */}
            <div className="col-md-5">
              <label className="form-label">Description</label>
              <input
                {...register("splitDescription")}
                type="text"
                className="form-control"
              />
            </div>

            {/* Add Line Button */}
            <div className="col-md-1 d-flex align-items-end">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleAddAllocation}
                disabled={
                  !formValues.splitLevel3 ||
                  formValues.splitLevel3.length === 0 ||
                  !formValues.splitAmount ||
                  formValues.splitAmount <= 0 ||
                  !allocationBasis
                }
              >
                Add Line
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Allocation Table */}
      <div className="card">
        <div className="card-header">
          <h5>Allocation Table</h5>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-bordered custom-table m-0">
              <thead>
                <tr>
                  <th>Sr.</th>
                  <th>Accounts</th>
                  <th>Description</th>
                  <th>Debit</th>
                  <th>Basis</th>
                  <th>Students</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {allocations.length > 0 ? (
                  allocations.map((item, index) => (
                    <tr key={item.id}>
                      <td>{index + 1}</td>
                      <td>{item.account}</td>
                      <td>{item.description}</td>
                      <td>{item.debit.toFixed(2)}</td>
                      <td>
                        {item.allocationBasis === "studentStrength"
                          ? "Student Strength"
                          : "Class Capacity"}
                      </td>
                      <td>{item.studentCount}</td>
                      <td>
                        <button
                          type="button"
                          className="btn p-0"
                          onClick={() => handleRemoveAllocation(item.id)}
                        >
                          <i className="fas fa-trash text-danger"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No allocations added yet
                    </td>
                  </tr>
                )}
              </tbody>
              {allocations.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan="3" className="text-end fw-bold">
                      Total Allocated:
                    </td>
                    <td className="fw-bold">
                      {allocations
                        .reduce((sum, item) => sum + item.debit, 0)
                        .toFixed(2)}
                    </td>
                    <td colSpan="3"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          <div className="mt-3 d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit(onSubmit)}
              disabled={allocations.length === 0 || remainingAmount > 0}
            >
              Save Expense Allocation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseAllocation;
